import { useTheme } from "@emotion/react";
import React, { useMemo, useState } from "react";
import { MdArrowDownward, MdArrowUpward } from "react-icons/md";

const numberFormat = new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 2 });

const display = (value: unknown) => {
  if (value === null || value === undefined) return "–";
  if (typeof value === "number") return numberFormat.format(value);
  return String(value);
};

const shortTextLength = 24;

const compare = (a: unknown, b: unknown) => {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "pl");
};

export const OutputTable: React.FC<{ columns: string[]; rows: unknown[][] }> = ({ columns, rows }) => {
  const theme = useTheme();
  const [sort, setSort] = useState<{ column: number; desc: boolean } | null>(null);

  const numericColumns = useMemo(
    () => columns.map((_, i) => rows.every((row) => row[i] === null || typeof row[i] === "number")),
    [columns, rows],
  );

  const shortColumns = useMemo(
    () => columns.map((_, i) => rows.every((row) => display(row[i]).length <= shortTextLength)),
    [columns, rows],
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) => compare(a[sort.column], b[sort.column]) * (sort.desc ? -1 : 1));
  }, [rows, sort]);

  const toggleSort = (column: number) =>
    setSort((s) => (s?.column === column ? (s.desc ? { column, desc: false } : null) : { column, desc: true }));

  return (
    <div
      data-output-table
      css={{
        maxHeight: "480px",
        overflow: "auto",
        border: "1px solid rgba(0, 0, 0, 0.06)",
        borderRadius: theme.smallBorderRadius,
      }}
    >
      <table css={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "0.8125rem" }}>
        <thead>
          <tr>
            {columns.map((column, i) => (
              <th
                key={i}
                onClick={() => toggleSort(i)}
                css={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  background: theme.colors.background,
                  textAlign: numericColumns[i] ? "right" : "left",
                  verticalAlign: "bottom",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: theme.colors.secondaryText,
                  padding: "0.5rem 0.625rem",
                  borderBottom: `1px solid ${theme.colors.border}`,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
              >
                <span css={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                  {column}
                  {sort?.column === i && (sort.desc ? <MdArrowDownward /> : <MdArrowUpward />)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                css={{
                  padding: "1.5rem 0.5rem",
                  textAlign: "center",
                  color: theme.colors.secondaryText,
                  background: theme.colors.background,
                }}
              >
                Brak danych w wybranym okresie
              </td>
            </tr>
          )}
          {sortedRows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              css={{
                "&:nth-of-type(even)": { background: "rgba(0, 0, 0, 0.018)" },
                "&:hover": { background: theme.colors.hover },
              }}
            >
              {row.map((value, i) => (
                <td
                  key={i}
                  css={{
                    textAlign: numericColumns[i] ? "right" : "left",
                    fontVariantNumeric: numericColumns[i] ? "tabular-nums" : undefined,
                    whiteSpace: shortColumns[i] ? "nowrap" : undefined,
                    verticalAlign: "top",
                    maxWidth: "28rem",
                    overflowWrap: "break-word",
                    padding: "0.4375rem 0.625rem",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  {display(value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
